<template>
    <v-container fluid>
        <v-form
            ref="form"
            v-model="validForm"
            lazy-validation
        >
            <v-row>
                <v-col 
                    style="display: flex; align-content: center; align-items: center;"
                    cols="12"
                >
                    <h3>Product Pricing Request</h3>
                </v-col>
            </v-row>
            <v-row>
                <v-col cols="12">
                    <hr />
                </v-col>
            </v-row>

            <!-- Project -->
            <v-row>
                <v-col cols="12">
                    <label>Project</label>
                    <v-combobox
                        v-model="request.projectSelected"
                        :items="listProjects"
                        item-text="description"
                        item-value="id"
                        clearable
                        single-line
                        outlined
                        dense
                    ></v-combobox>
                </v-col>
            </v-row>

            <v-row>
                <v-col cols="12">
                    <hr />
                </v-col>
            </v-row>

            <!-- Product Items -->
            <v-row>
                <v-col 
                    cols="12"
                    style="display: flex; align-items: center; justify-content: space-between;"
                >
                    <label style="font-weight: 600; font-size: 1rem;">Products</label>
                    <v-btn small color="var(--color__main)" dark @click="addItem">
                        <v-icon small left>mdi-plus</v-icon>
                        Add Product
                    </v-btn>
                </v-col>
            </v-row>
            <v-row>
                <v-col cols="12">
                    <v-simple-table dense>
                        <template v-slot:default>
                            <thead>
                                <tr>
                                    <th style="width: 40%">Product Name *</th>
                                    <th>Description / Notes</th>
                                    <th style="width: 60px; text-align: center;">Remove</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr
                                    v-if="request.listItems.length === 0"
                                >
                                    <td colspan="3" style="text-align: center; padding: 16px; color: grey;">
                                        No products added yet. Click "Add Product" to begin.
                                    </td>
                                </tr>
                                <tr
                                    v-for="(item, index) in request.listItems"
                                    :key="index"
                                >
                                    <td>
                                        <v-text-field
                                            v-model="item.name"
                                            :rules="[validations.required]"
                                            single-line
                                            outlined
                                            dense
                                            hide-details="auto"
                                            class="mt-1 mb-1"
                                        ></v-text-field>
                                    </td>
                                    <td>
                                        <v-text-field
                                            v-model="item.description"
                                            single-line
                                            outlined
                                            dense
                                            hide-details="auto"
                                            class="mt-1 mb-1"
                                        ></v-text-field>
                                    </td>
                                    <td style="text-align: center;">
                                        <v-btn icon small color="var(--color__red)" @click="removeItem(index)">
                                            <v-icon small>mdi-delete</v-icon>
                                        </v-btn>
                                    </td>
                                </tr>
                            </tbody>
                        </template>
                    </v-simple-table>
                </v-col>
            </v-row>

            <v-row>
                <v-col cols="12">
                    <hr />
                </v-col>
            </v-row>

            <!-- Attachments -->
            <v-row>
                <v-col cols="12">
                    <canvas id="canvas" hidden></canvas>
                    <label>Attachments</label>
                    <v-file-input
                        v-model="requestFiles"
                        accept=".pdf,image/*"
                        small-chips
                        multiple
                        outlined
                        placeholder="Click here to add files"
                        prepend-inner-icon="mdi-paperclip"
                        prepend-icon=""
                        @change="resizePreviewImage(requestFiles, requestDocs)"
                    ></v-file-input>
                </v-col>
            </v-row>
            <v-row>
                <v-col
                    v-for="(itemDoc, index) in requestDocs.filter(doc => doc.deleted == false)"
                    :key="index"
                    cols="12"
                    lg="1"
                    md="4"
                    sm="12"
                >
                    <v-card
                        v-if="itemDoc.type.toString().toLowerCase().includes('pdf')"
                        max-width="200"
                        class="justify-center"
                        @click="downloadFile(URL_ATTACHMENT, itemDoc.id, itemDoc.type, itemDoc.fileNameComplete)"
                    >
                        <v-card-text style="height: 129px; text-align: center;">
                            <v-icon x-large>mdi mdi-file-pdf-box</v-icon>
                            <br />
                            PDF File
                        </v-card-text>
                        <span class="imageFileName">{{ itemDoc.fileName }}</span>
                        <v-icon class="deletePhotoIcon" small right color="var(--color__red)" @click.stop="" @click="confirmDocDelete(itemDoc)">mdi mdi-delete</v-icon>
                    </v-card>

                    <v-card v-else max-width="200">
                        <v-img
                            :src="itemDoc.src"
                            :lazy-src="itemDoc.src"
                            aspect-ratio="1"
                            max-height="200"
                            max-width="200"
                            style="cursor: pointer;"
                            @click="downloadFile(URL_ATTACHMENT, itemDoc.id, itemDoc.type, itemDoc.fileNameComplete)"
                        >
                            <template v-slot:placeholder>
                                <v-row class="fill-height ma-0" align="center" justify="center">
                                    <v-progress-circular indeterminate color="grey lighten-5"></v-progress-circular>
                                </v-row>
                            </template>
                        </v-img>
                        <span class="imageFileName">{{ itemDoc.fileName }}</span>
                        <v-icon class="deletePhotoIcon" small right color="var(--color__red)" @click="confirmDocDelete(itemDoc)">mdi mdi-delete</v-icon>
                    </v-card>
                </v-col>
            </v-row>

            <ActionButtons
                :request="request"
                :loading="loading"
                :showButtons="{
                    cancel: true,
                    save: true
                }"
                @cancel="cancel"
                @save="save"
            />

            <ActionDialog 
                v-on:methodConfirmToCall="dialog.methodConfirm"
                :showDialog.sync="dialog.show"
                :headerTitle="dialog.headerText"
                :bodyText="dialog.bodyText"
                :params="dialog.params"
            />
        </v-form>
    </v-container>
</template>

<script>
    import { required } from "@/utilities/Rules";
    import Helpers from '@/utilities/Helpers';
    import ActionButtons from "@/components/Layout/ActionButtons";
    import ActionDialog from "@/components/Layout/ActionDialog";
    import { 
        TYPE_DOCUMENT, 
        TYPE_ATTACHMENT,
        URL_ATTACHMENT,
    } from "@/utilities/Enums";

    export default ({

        mixins: [Helpers],

        components: {
            ActionButtons,
            ActionDialog,
        },

        data: () => ({

            validForm: true,

            requestFiles: [],
            requestDocs: [],

            itemDocToDelete: null,

            listProjects: [],

            request: {
                id: 0,
                projectSelected: null,
                status: 0,
                listItems: [],
            },

            loading: false,

            validations: {
                required: required,
            },

            dialog: {
                show: false,
                headerText: "",
                bodyText: "",
                methodConfirm: ( () => {} ),
                params: null
            },

            URL_ATTACHMENT,
        }),

        props: {
            id: {
                default: 0
            }
        },

        methods: {

            addItem() {
                this.request.listItems.push({ id: 0, name: "", description: "" });
            },

            removeItem(index) {
                this.request.listItems.splice(index, 1);
            },

            async confirmDocDelete(itemDocToDelete) {
                this.itemDocToDelete = itemDocToDelete;
                this.dialog = {
                    show: true,
                    headerText: 'Confirmation',
                    bodyText: `You will DELETE this file [${itemDocToDelete.fileName}], confirm your decision?`,
                    methodConfirm: this.deleteDoc,
                    params: 0
                };
            },

            async deleteDoc() {
                this.itemDocToDelete.deleted = true;
            },

            async getRegister() {
                if (this.id !== 0) {
                    const response = await this.$store.dispatch("productPricingRequestModule/GetById", this.id);

                    if (response.success !== false) {
                        const record = response.result || response;
                        this.request = {
                            id: record.id || this.id,
                            projectSelected: record.idServiceProject
                                ? this.listProjects.find(p => p.id === record.idServiceProject) || null
                                : null,
                            status: record.status || 0,
                            listItems: (record.listItems || []).map(item => ({
                                id: item.id || 0,
                                name: item.name || "",
                                description: item.description || "",
                            })),
                        };

                        this.requestFiles = [];
                        this.requestDocs = [];

                        const listAttachments = await this.$store.dispatch("attachmentModule/GetAll", { 
                            typeAttachment: TYPE_ATTACHMENT.PRODUCT_PRICING_REQUEST.value, 
                            idKey: this.id 
                        });

                        listAttachments.forEach(itemDoc => {
                            this.requestDocs.push({
                                id: itemDoc.id,
                                file: new File([], itemDoc.fileName),
                                type: itemDoc.fileType,
                                fileName: itemDoc.fileNameShort,
                                fileNameComplete: itemDoc.fileName,
                                deleted: false,
                                src: `data:image/jpg;base64,${itemDoc.file}`
                            });
                        });
                    }
                }
            },

            async cancel() {
                this.$router.push({ path: "/sales/productPricingRequestList" });
            },

            async uploadFiles(id, idKey, file, typeDocument, deleted = false) {
                const attachmentRequest = {
                    id: id,
                    idKey: idKey,
                    file: file ? file : new Blob(),
                    fileName: file ? file.name : "",
                    fileType: file ? file.type : "",
                    fileSize: file ? file.size : 0,
                    deleted: deleted ? deleted : false,
                    typeAttachment: TYPE_ATTACHMENT.PRODUCT_PRICING_REQUEST.value,
                    typeDocument: typeDocument,
                    idKeyReference: 0
                };

                if (attachmentRequest.fileName != null && attachmentRequest.fileName != undefined) {
                    let attachmentFileRequest = new FormData();
                    attachmentFileRequest.append("attachmentRequest", JSON.stringify(attachmentRequest));
                    attachmentFileRequest.append("attachmentFile", attachmentRequest.file, attachmentRequest.fileName);

                    const result = await this.$store.dispatch("attachmentModule/CreateUpdate", { 
                        attachmentFileRequest, 
                        id: 0
                    });

                    return result;
                }
                else {
                    return { success: true };
                }
            },

            async saveDocs(idKey) {
                let allResult = [];

                for (const itemDoc of this.requestDocs) {
                    const result = await this.uploadFiles(
                        itemDoc.id, 
                        idKey, 
                        itemDoc.file, 
                        TYPE_DOCUMENT.PRODUCT_PRICING_REQUEST_DOCS.value, 
                        itemDoc.deleted
                    );

                    if (!result.success) {
                        allResult.push({ message: result.message });
                    }
                }

                if (allResult.length > 0) {
                    let message = allResult.map(r => r.message).join('\n');
                    this.showToast("error", "Warning!", message);
                    return false;
                }

                return true;
            },

            async save() {
                await this.$refs.form.validate();

                if (this.validForm === false) {
                    this.showToast("error", "Warning!", "There are inconsistencies in the form. Please review!");
                    return;
                }

                if (this.request.listItems.length === 0) {
                    this.showToast("error", "Warning!", "At least one product item is required.");
                    return;
                }

                this.showLoading();

                const saveRequest = {
                    id: this.request.id,
                    idServiceProject: this.request.projectSelected ? this.request.projectSelected.id : null,
                    listItems: this.request.listItems.map(item => ({
                        id: item.id || 0,
                        name: item.name,
                        description: item.description || "",
                    })),
                };

                const result = await this.$store.dispatch("productPricingRequestModule/CreateUpdate", saveRequest);

                if (result.success) {
                    const idKey = result.id || this.request.id;

                    await this.saveDocs(idKey);

                    this.showToast("success", "Success!", result.message);
                    this.$router.push({ path: "/sales/productPricingRequestList" });
                }
                else {
                    this.showToast("error", "Error!", result.message);
                }

                this.hideLoading();
            },

            async getLists() {
                this.listProjects = await this.$store.dispatch("serviceModule/ListProjectCompact", "-");
            },
        },

        async created() {
            this.showLoading();
            await this.getLists();
            await this.getRegister();
            this.hideLoading();
        }
    })
</script>
